import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Sparkles, MessageCircle, Lock, Check, Shield, Users, Zap, Download, Smartphone } from 'lucide-react';
import { isAuthenticated } from '../utils/auth';

export default function Landing() {
  const navigate = useNavigate();
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [deviceType, setDeviceType] = useState('unknown'); // 'ios', 'android', 'desktop'

  useEffect(() => {
    // Detect device type
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      setDeviceType('ios');
    } else if (/android/i.test(userAgent)) {
      setDeviceType('android');
    } else {
      setDeviceType('desktop');
    }

    // PWA Install Prompt Handler
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show custom install prompt after 5 seconds
      setTimeout(() => {
        if (!window.matchMedia('(display-mode: standalone)').matches) {
          setShowInstallPrompt(true);
        }
      }, 5000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Fallback for browsers that don't support beforeinstallprompt
    const timer = setTimeout(() => {
      if (window.matchMedia('(max-width: 768px)').matches && !window.matchMedia('(display-mode: standalone)').matches) {
        // Only show if not already in standalone mode
        setShowInstallPrompt(true);
      }
    }, 10000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearTimeout(timer);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User response to install prompt: ${outcome}`);
        setDeferredPrompt(null);
      } catch (error) {
        console.error('Install prompt error:', error);
      }
    } else {
      // Fallback for iOS and browsers without beforeinstallprompt
      // Show manual instructions
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);
      
      if (isIOS) {
        alert('To install BlindMatch:\n\n1. Tap the Share button (square with arrow)\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add"');
      } else if (isAndroid) {
        alert('To install BlindMatch:\n\n1. Tap the menu (3 dots) in Chrome\n2. Tap "Add to Home Screen"\n3. Tap "Add"');
      } else {
        alert('To install BlindMatch, look for "Install" option in your browser menu or address bar.');
      }
    }
    setShowInstallPrompt(false);
  };

  return (
    <div className="app-container gradient-bg">
      <div className="px-4 py-6 pb-20">
        {/* Install Prompt */}
        {showInstallPrompt && (
          <div className="fixed top-4 left-4 right-4 z-50 glass rounded-2xl p-4 border border-primary/30 shadow-lg shadow-primary/20" data-testid="install-prompt">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Heart className="text-primary fill-primary" size={24} />
                <div>
                  <p className="text-white font-semibold text-sm">Install BlindMatch</p>
                  <p className="text-gray-400 text-xs">Secure chats with push notifications</p>
                </div>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={handleInstallClick}
                  className="bg-primary text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-primary/90"
                  data-testid="install-button"
                >
                  Install
                </button>
                <button 
                  onClick={() => setShowInstallPrompt(false)} 
                  className="text-gray-400 hover:text-white px-2"
                  data-testid="dismiss-install"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header with Float Animation */}
        <div className="text-center mb-8 float-animation">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Heart className="text-primary fill-primary pulse-animation" size={40} />
            <h1 className="text-5xl font-bold text-white">BlindMatch</h1>
          </div>
          <p className="text-gray-400 text-lg">
            Anonymous chats. Real connections. Reveal when ready.
          </p>
        </div>

        {/* Trust Badge */}
        <div className="text-center mb-8">
          <div className="inline-block glass rounded-full px-6 py-3 border border-primary/30">
            <p className="text-amber-400 font-bold text-xl shimmer" data-testid="landing-trust-badge">
              100% anonymous · Verified profiles
            </p>
          </div>
        </div>

        {/* Value Proposition - Why BlindMatch */}
        <div className="glass rounded-3xl p-6 mb-8 border border-primary/20">
          <h2 className="text-white font-bold text-xl mb-4 text-center">Dating apps broken?</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-red-400">😫</span>
              </div>
              <div>
                <p className="text-white font-medium">Tired of endless swiping?</p>
                <p className="text-gray-400 text-sm">Get quality over quantity - every match counts</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-red-400">🙄</span>
              </div>
              <div>
                <p className="text-white font-medium">Flooded with low-effort messages?</p>
                <p className="text-gray-400 text-sm">Paid entry = serious people only</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-green-400">✨</span>
              </div>
              <div>
                <p className="text-white font-medium">BlindMatch is different</p>
                <p className="text-gray-400 text-sm">Chat anonymously, reveal when YOU feel comfortable</p>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Cards with Enhanced Style */}
        <div className="grid gap-4 mb-8">
          <div className="card-enhanced rounded-3xl p-6" data-testid="feature-card-anonymous">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-primary/20 flex items-center justify-center">
                <Shield className="text-primary" size={28} />
              </div>
              <div>
                <h3 className="text-white font-bold text-xl mb-1">Stay Anonymous</h3>
                <p className="text-gray-400 text-sm">No names or photos until YOU decide. Your identity, your control.</p>
              </div>
            </div>
          </div>
          
          <div className="card-enhanced rounded-3xl p-6" data-testid="feature-card-control">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-green-500/20 flex items-center justify-center">
                <Lock className="text-green-400" size={28} />
              </div>
              <div>
                <h3 className="text-white font-bold text-xl mb-1">End-to-End Encrypted</h3>
                <p className="text-gray-400 text-sm">Your chats are private. Not even we can read them. Military-grade encryption.</p>
              </div>
            </div>
          </div>

          <div className="card-enhanced rounded-3xl p-6" data-testid="feature-card-verified">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-blue-500/20 flex items-center justify-center">
                <Check className="text-blue-400" size={28} />
              </div>
              <div>
                <h3 className="text-white font-bold text-xl mb-1">Verified Profiles</h3>
                <p className="text-gray-400 text-sm">Optional photo verification for added trust. Real people, verified.</p>
              </div>
            </div>
          </div>

          <div className="card-enhanced rounded-3xl p-6" data-testid="feature-card-quality">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-amber-500/20 flex items-center justify-center">
                <Zap className="text-amber-400" size={28} />
              </div>
              <div>
                <h3 className="text-white font-bold text-xl mb-1">Quality Matches</h3>
                <p className="text-gray-400 text-sm">Smart algorithm matches based on your primary interests and compatibility.</p>
              </div>
            </div>
          </div>
        </div>

        {/* How it Works */}
        <div className="mb-8">
          <h2 className="text-white font-bold text-3xl mb-6 text-center">How it works</h2>
          <div className="space-y-4">
            <div className="flex gap-4 items-start glass rounded-2xl p-4">
              <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                1
              </div>
              <div>
                <h4 className="text-white font-semibold text-lg">Create profile anonymously</h4>
                <p className="text-gray-400 text-sm">Just your interests, no personal info shared</p>
              </div>
            </div>
            
            <div className="flex gap-4 items-start glass rounded-2xl p-4">
              <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                2
              </div>
              <div>
                <h4 className="text-white font-semibold text-lg">Get your matches</h4>
                <p className="text-gray-400 text-sm">Choose 3 or 10 match packages based on your preference.</p>
              </div>
            </div>
            
            <div className="flex gap-4 items-start glass rounded-2xl p-4">
              <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                3
              </div>
              <div>
                <h4 className="text-white font-semibold text-lg">Chat and reveal when ready</h4>
                <p className="text-gray-400 text-sm">Share contact details only when you feel comfortable</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA with Glow */}
        <button
          onClick={() => navigate('/signup')}
          className="w-full h-14 bg-accent hover:bg-accent/90 text-background font-bold text-lg rounded-full btn-glow mb-4 flex items-center justify-center gap-2"
          data-testid="landing-cta-button"
        >
          <Sparkles size={20} />
          Get Your Blind Matches
        </button>

        <button
          onClick={() => navigate('/login')}
          className="w-full text-center text-gray-400 hover:text-white transition-colors"
          data-testid="landing-login-link"
        >
          Already a member? Log in
        </button>

        {/* Trust Line */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full">
            <Lock size={16} className="text-green-400" />
            <p className="text-sm text-gray-400">
              Secure payment. Anonymous forever until YOU choose.
            </p>
          </div>
        </div>

        {/* Download App Section */}
        <div className="mt-8 glass rounded-3xl p-6 border border-primary/20">
          <div className="text-center mb-4">
            <Smartphone className="text-primary mx-auto mb-2" size={32} />
            <h3 className="text-white font-bold text-xl">Install the App</h3>
            <p className="text-gray-400 text-sm">Add BlindMatch to your home screen for the best experience</p>
          </div>
          
          {/* PWA Install Button */}
          <button
            onClick={handleInstallClick}
            className="w-full flex items-center justify-center gap-3 px-5 py-4 bg-primary hover:bg-primary/90 rounded-xl transition-all duration-300 mb-3"
            data-testid="pwa-install-button"
          >
            <Download size={24} className="text-white" />
            <div className="text-left">
              <p className="text-white font-semibold">Install BlindMatch</p>
              <p className="text-white/70 text-xs">Works offline • Fast • No app store needed</p>
            </div>
          </button>

          <div className="flex items-center gap-3 justify-center text-gray-500 text-xs">
            <div className="flex items-center gap-1">
              <Check size={12} className="text-green-400" />
              <span>Instant install</span>
            </div>
            <div className="flex items-center gap-1">
              <Check size={12} className="text-green-400" />
              <span>Push notifications</span>
            </div>
            <div className="flex items-center gap-1">
              <Check size={12} className="text-green-400" />
              <span>Works offline</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 space-y-4 text-center">
          <div className="flex justify-center flex-wrap gap-4 text-sm">
            <button onClick={() => navigate('/terms')} className="text-gray-400 hover:text-white">
              Terms
            </button>
            <button onClick={() => navigate('/privacy')} className="text-gray-400 hover:text-white">
              Privacy
            </button>
            <button onClick={() => navigate('/refund-policy')} className="text-gray-400 hover:text-white">
              Refund Policy
            </button>
            <button onClick={() => navigate('/delivery-policy')} className="text-gray-400 hover:text-white">
              Delivery
            </button>
            <button onClick={() => navigate('/contact')} className="text-gray-400 hover:text-white">
              Contact Us
            </button>
          </div>
          <p className="text-gray-500 text-xs">© 2026 BlindMatch. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
